import { useEffect, useMemo, useState } from 'react'
import { loadPrintPinArtwork } from '../../lib/mapPrintClient'
import { pinTypeVisibility } from '../../lib/mapPinTypes'
import type { MapAppearance } from '../../lib/mapAppearance'
import type { TeamMap } from '../../lib/types'

const NO_SVGS: Record<string, string> = {}

/**
 * Load while print mode opens so missing artwork is known before submission.
 * The with-background drawings come too: they are what a pin's stored colour is
 * compared against to tell an override from the type's own colour.
 */
export function usePrintArtwork(
  map: TeamMap,
  appearance: MapAppearance,
  hiddenObjectIds: ReadonlySet<string>,
  enabled = true,
) {
  const key = useMemo(() => {
    const visible = pinTypeVisibility(appearance.hiddenPinTypes)
    const types = [
      ...new Set(
        (map.objects ?? [])
          .filter((p) => visible(p.type) && !hiddenObjectIds.has(p.id))
          .map((p) => p.type),
      ),
    ].sort()
    return JSON.stringify([appearance.pinBackground, types])
  }, [
    map.objects,
    appearance.pinBackground,
    appearance.hiddenPinTypes,
    hiddenObjectIds,
  ])
  const [loaded, setLoaded] = useState<{
    key: string
    svgs: Record<string, string>
    designSvgs: Record<string, string>
  } | null>(null)
  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    const [withBackground, types] = JSON.parse(key) as [boolean, string[]]
    if (types.length === 0) {
      setLoaded({ key, svgs: NO_SVGS, designSvgs: NO_SVGS })
      return
    }
    void loadPrintPinArtwork(types, { withBackground }).then(({ symbolSvgs, symbolDesignSvgs }) => {
      if (!cancelled) setLoaded({ key, svgs: symbolSvgs, designSvgs: symbolDesignSvgs })
    })
    return () => {
      cancelled = true
    }
  }, [key, enabled])
  const current = loaded?.key === key ? loaded : null
  return {
    pending: enabled && current === null,
    svgs: current?.svgs ?? NO_SVGS,
    designSvgs: current?.designSvgs ?? NO_SVGS,
  }
}
